// MCP server OAuth (RFC 7591 Dynamic Client Registration + RFC 6749
// Authorization Code flow with PKCE).
//
// Flow on every successful skapi login:
//   1. POST /oauth/register             -> { client_id, client_secret }
//   2. Generate PKCE pair + state
//   3. window.location -> /oauth/authorize?... (browser leaves the SPA)
//   4. MCP redirects back to <origin>/login?code=...&state=...
//   5. completeMcpAuthorize() POSTs /oauth/token, stores tokens locally
//   6. router.push() to wherever the user was headed (/my-projects).
//
// Storage layout (all localStorage unless noted):
//   mcp_oauth_client        -> { client_id, client_secret, registered_at }
//   mcp_oauth_token         -> { access_token, refresh_token?, expires_at }
//   sessionStorage:
//     mcp_oauth:state       -> { codeVerifier, redirectAfter }   (single in-flight flow)
const isDev = (import.meta.env.VITE_MODE === "development");

export const MCP_BASE_URL = isDev ? 'https://mcp-dev.broadwayinc.computer' : 'https://mcp.broadwayinc.computer';
const MCP_REGISTER_URL = `${MCP_BASE_URL}/oauth/register`;
const MCP_AUTHORIZE_URL = `${MCP_BASE_URL}/oauth/authorize`;
const MCP_TOKEN_URL = `${MCP_BASE_URL}/oauth/token`;

// v2 suffix bumped when the registered redirect_uri changed from
// /oauth-login to /login, so old clients get re-registered automatically.
const CLIENT_STORAGE_KEY = 'mcp_oauth_client_v2';
const TOKEN_STORAGE_KEY = 'mcp_oauth_token_v2';
const STATE_SESSION_KEY = 'mcp_oauth:state';

export type McpOAuthClient = {
	client_id: string;
	client_secret?: string;
	registered_at: number;
	[k: string]: any;
};

export type McpOAuthToken = {
	access_token: string;
	refresh_token?: string;
	token_type?: string;
	expires_at?: number; // epoch ms
	[k: string]: any;
};

const getRedirectUri = () => `${window.location.origin}/login`;

export const getStoredMcpClient = (): McpOAuthClient | null => {
	try {
		const raw = localStorage.getItem(CLIENT_STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

export const getStoredMcpToken = (): McpOAuthToken | null => {
	try {
		const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

export const clearStoredMcpToken = () => {
	localStorage.removeItem(TOKEN_STORAGE_KEY);
};

// ---- PKCE helpers ----------------------------------------------------------

const base64UrlEncode = (bytes: Uint8Array): string => {
	let str = '';
	for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
	return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateCodeVerifier = (): string => {
	const bytes = new Uint8Array(32);
	crypto.getRandomValues(bytes);
	return base64UrlEncode(bytes);
};

const generateCodeChallenge = async (
	verifier: string,
): Promise<{ challenge: string; method: 'S256' | 'plain' }> => {
	if (typeof crypto !== 'undefined' && crypto.subtle && typeof crypto.subtle.digest === 'function') {
		try {
			const data = new TextEncoder().encode(verifier);
			const hash = await crypto.subtle.digest('SHA-256', data);
			return { challenge: base64UrlEncode(new Uint8Array(hash)), method: 'S256' };
		} catch (e) {
			// fall through to plain
		}
	}
	// Fallback for insecure contexts (no crypto.subtle): PKCE 'plain' method.
	return { challenge: verifier, method: 'plain' };
};

const generateState = (): string => {
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);
	return base64UrlEncode(bytes);
};

// ---- 1. Register -----------------------------------------------------------

export const registerMcpClient = async (): Promise<McpOAuthClient> => {
	const body = {
		client_name: 'bunnyquery',
		grant_types: ['authorization_code', 'refresh_token'],
		response_types: ['code'],
		redirect_uris: [getRedirectUri()],
		token_endpoint_auth_method: 'client_secret_basic',
	};

	const res = await fetch(MCP_REGISTER_URL, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
	});

	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`MCP /oauth/register failed: ${res.status} ${text}`);
	}

	const json = (await res.json()) as Record<string, any>;
	if (!json.client_id) {
		throw new Error('MCP /oauth/register response missing client_id');
	}

	const stored: McpOAuthClient = {
		...json,
		client_id: json.client_id,
		client_secret: json.client_secret,
		registered_at: Date.now(),
	};
	localStorage.setItem(CLIENT_STORAGE_KEY, JSON.stringify(stored));
	return stored;
};

// ---- 2/3. Authorize redirect ----------------------------------------------

export const startMcpAuthorize = async (
	client: McpOAuthClient,
	redirectAfter = '/my-projects',
): Promise<void> => {
	const codeVerifier = generateCodeVerifier();
	const { challenge: codeChallenge, method: codeChallengeMethod } = await generateCodeChallenge(codeVerifier);
	const state = generateState();

	sessionStorage.setItem(
		STATE_SESSION_KEY,
		JSON.stringify({ state, codeVerifier, redirectAfter }),
	);

	const params = new URLSearchParams({
		response_type: 'code',
		client_id: client.client_id,
		redirect_uri: getRedirectUri(),
		state,
		code_challenge: codeChallenge,
		code_challenge_method: codeChallengeMethod,
	});

	window.location.href = `${MCP_AUTHORIZE_URL}?${params.toString()}`;
};

export const beginMcpOAuthOnLogin = async (
	redirectAfter = '/my-projects',
): Promise<void> => {
	const client = await registerMcpClient();
	await startMcpAuthorize(client, redirectAfter);
};

// ---- 5. Token exchange -----------------------------------------------------

const basicAuthHeader = (clientId: string, clientSecret: string): string =>
	'Basic ' + btoa(`${clientId}:${clientSecret}`);

export const isMcpOAuthCallback = (query: Record<string, any>): boolean => {
	if (!query?.code || !query?.state) return false;
	try {
		const raw = sessionStorage.getItem(STATE_SESSION_KEY);
		if (!raw) return false;
		const stored = JSON.parse(raw);
		return stored?.state === query.state;
	} catch {
		return false;
	}
};

export const completeMcpAuthorize = async (
	query: Record<string, any>,
): Promise<{ token: McpOAuthToken; redirectAfter: string }> => {
	const raw = sessionStorage.getItem(STATE_SESSION_KEY);
	if (!raw) throw new Error('Missing MCP OAuth state');
	const stored = JSON.parse(raw) as {
		state: string;
		codeVerifier: string;
		redirectAfter: string;
	};
	sessionStorage.removeItem(STATE_SESSION_KEY);

	if (stored.state !== query.state) {
		throw new Error('MCP OAuth state mismatch');
	}

	const client = getStoredMcpClient();
	if (!client) throw new Error('No registered MCP client');

	const body = new URLSearchParams({
		grant_type: 'authorization_code',
		code: String(query.code),
		redirect_uri: getRedirectUri(),
		code_verifier: stored.codeVerifier,
		client_id: client.client_id,
	});

	const headers: Record<string, string> = {
		'Content-Type': 'application/x-www-form-urlencoded',
	};
	if (client.client_secret) {
		headers.Authorization = basicAuthHeader(client.client_id, client.client_secret);
	}

	const res = await fetch(MCP_TOKEN_URL, {
		method: 'POST',
		headers,
		body: body.toString(),
	});

	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`MCP /oauth/token failed: ${res.status} ${text}`);
	}

	const json = (await res.json()) as Record<string, any>;
	if (!json.access_token) {
		throw new Error('MCP /oauth/token response missing access_token');
	}

	const token: McpOAuthToken = {
		...json,
		access_token: json.access_token,
		refresh_token: json.refresh_token,
		token_type: json.token_type,
		expires_at:
			typeof json.expires_in === 'number'
				? Date.now() + json.expires_in * 1000
				: undefined,
	};

	localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(token));

	return { token, redirectAfter: stored.redirectAfter || '/my-projects' };
};

// ---- Optional: refresh -----------------------------------------------------

// Dedupe concurrent refreshes. A refresh_token grant can be single-use (rotated
// on success), so two overlapping calls race: the loser's POST fails and
// clearStoredMcpToken() (below) wipes the grant, which then makes maybeReauthMcp
// force a full OAuth redirect and stall in-SPA navigation. This bites when
// navigating back to the project list while the agent chat is still loading —
// the chat's session recovery (ensureMcpGrantFresh) and the router's checkUser
// guard (maybeReauthMcp) BOTH call this at once, so the full-screen loader gets
// stuck. Sharing one in-flight promise makes both callers reuse a single refresh.
let _mcpRefreshInFlight: Promise<McpOAuthToken | null> | null = null;

export const refreshMcpToken = (): Promise<McpOAuthToken | null> => {
	if (_mcpRefreshInFlight) return _mcpRefreshInFlight;
	_mcpRefreshInFlight = _refreshMcpTokenOnce().finally(() => {
		_mcpRefreshInFlight = null;
	});
	return _mcpRefreshInFlight;
};

const _refreshMcpTokenOnce = async (): Promise<McpOAuthToken | null> => {
	const client = getStoredMcpClient();
	const current = getStoredMcpToken();
	if (!client || !current?.refresh_token) return null;

	const body = new URLSearchParams({
		grant_type: 'refresh_token',
		refresh_token: current.refresh_token,
		client_id: client.client_id,
	});

	const headers: Record<string, string> = {
		'Content-Type': 'application/x-www-form-urlencoded',
	};
	if (client.client_secret) {
		headers.Authorization = basicAuthHeader(client.client_id, client.client_secret);
	}

	const res = await fetch(MCP_TOKEN_URL, {
		method: 'POST',
		headers,
		body: body.toString(),
	});

	if (!res.ok) {
		clearStoredMcpToken();
		return null;
	}

	const json = (await res.json()) as Record<string, any>;
	if (!json.access_token) return null;

	const token: McpOAuthToken = {
		...json,
		access_token: json.access_token,
		refresh_token: json.refresh_token || current.refresh_token,
		expires_at:
			typeof json.expires_in === 'number'
				? Date.now() + json.expires_in * 1000
				: undefined,
	};
	localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(token));
	return token;
};
